import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  Checkbox,
  Chip,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { grey, indigo, purple } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import React from 'react';

import { BoardPerson, BoardResponse } from '../../api/ach/board';

// A person in the people filter: one entry per human, carrying every role they hold.
// Roles are OPEN display metadata, never a filter dimension — a role the wire invents
// tomorrow gets a chip and a section here with no FE change.
export interface PersonOption {
  id: number;
  name: string;
  roles: string[];
  // Faceted issue count; absent on the row-scraping fallback roster.
  count?: number;
}

const UNASSIGNED = 'unassigned';
// Bucket for a person the wire lists with no role at all, so they stay selectable.
const OTHER_ROLE = 'other';

interface RoleMeta {
  abbr: string;
  heading: string;
  color: string;
}

// Known roles in display order: chip abbreviation, section heading, chip hue. The hues
// stay off the severity ramp — red/orange/yellow/green already mean severity on this
// page, so a green chip would read as a status signal.
const ROLE_META: Record<string, RoleMeta> = {
  trafficker: { abbr: 'TRF', heading: 'Traffickers', color: indigo[500] },
  account_manager: { abbr: 'AM', heading: 'Account managers', color: purple[400] },
};

const humanize = (role: string): string => role.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// Unknown roles fall back to their humanized wire name in a neutral hue.
const roleMeta = (role: string): RoleMeta =>
  ROLE_META[role] ?? { abbr: humanize(role), heading: humanize(role), color: grey[600] };

// Known roles first in their fixed order, unknown ones appended alphabetically, so the
// chips and the section order never reshuffle between polls.
export function orderRoles(roles: string[]): string[] {
  const present = new Set(roles);
  return [
    ...Object.keys(ROLE_META).filter((r) => present.has(r)),
    ...[...present].filter((r) => !(r in ROLE_META)).sort(),
  ];
}

const rolesOf = (person: PersonOption): string[] => (person.roles?.length ? person.roles : [OTHER_ROLE]);

// peopleInBoard collects the distinct people assigned anywhere in the response. It is
// the FALLBACK roster, used only when the server has no counts.by_person facet: it can
// only see the people the current filters left on the board.
export function peopleInBoard(data?: BoardResponse): PersonOption[] {
  const byId = new Map<number, PersonOption>();
  const add = (person: BoardPerson | null | undefined, role: string) => {
    if (!person) {
      return;
    }
    const entry = byId.get(person.id) ?? { id: person.id, name: person.name, roles: [] };
    if (!entry.roles.includes(role)) {
      entry.roles.push(role);
    }
    byId.set(person.id, entry);
  };
  data?.contracts.forEach((c) =>
    c.line_items.forEach((li) =>
      li.issues.forEach((i) => {
        add(i.trafficker, 'trafficker');
        add(i.account_manager, 'account_manager');
      })
    )
  );
  return [...byId.values()];
}

// One menu row per (person, role) plus the single Unassigned row. A dual-role person
// therefore has TWO rows; selection is keyed by person id so they check and clear
// together and the wire only ever carries the id once.
type PeopleRow =
  | { key: string; kind: 'person'; person: PersonOption; role: string }
  | { key: string; kind: 'unassigned' };

function summaryLabel(selected: PersonOption[], unassigned: boolean): string {
  if (unassigned) {
    return 'Unassigned';
  }
  if (selected.length === 1) {
    return selected[0].name;
  }
  // Distinct people: a dual-role person picked once reads "People (1)".
  return selected.length ? `People (${selected.length})` : 'All people';
}

function RoleChip({ role }: { role: string }) {
  const meta = roleMeta(role);
  return (
    <Chip
      size="small"
      label={meta.abbr}
      data-testid={`role-chip-${role}`}
      sx={{
        height: 17,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        color: meta.color,
        bgcolor: alpha(meta.color, 0.12),
        '& .MuiChip-label': { px: 0.625 },
      }}
    />
  );
}

interface PeopleFilterProps {
  options: PersonOption[];
  selected: PersonOption[];
  unassigned: boolean;
  disabled: boolean;
  onTogglePerson: (person: PersonOption) => void;
  onToggleUnassigned: () => void;
  onClear: () => void;
}

// PeopleFilter is the searchable, role-grouped people facet. Search is client-side over
// the already-loaded roster: it filters people first, then MUI regroups what survives,
// so a section with no match is not rendered at all.
export default function PeopleFilter({
  options,
  selected,
  unassigned,
  disabled,
  onTogglePerson,
  onToggleUnassigned,
  onClear,
}: PeopleFilterProps) {
  const selectedIds = React.useMemo(() => new Set(selected.map((p) => p.id)), [selected]);

  const rows = React.useMemo(() => {
    const all: PeopleRow[] = [{ key: UNASSIGNED, kind: 'unassigned' }];
    orderRoles(options.flatMap(rolesOf)).forEach((role) => {
      options
        .filter((p) => rolesOf(p).includes(role))
        .forEach((person) => all.push({ key: `${role}:${person.id}`, kind: 'person', person, role }));
    });
    return all;
  }, [options]);

  const value = React.useMemo(
    () => rows.filter((r) => (r.kind === 'unassigned' ? unassigned : selectedIds.has(r.person.id))),
    [rows, unassigned, selectedIds]
  );

  // Every toggle is driven off details.option, never off the emitted array: MUI would
  // otherwise drop only the clicked row of a dual-role person and leave them selected.
  const onChange = (
    _event: React.SyntheticEvent,
    _next: PeopleRow[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<PeopleRow>
  ) => {
    if (reason === 'clear') {
      onClear();
      return;
    }
    const row = details?.option;
    if (!row) {
      return;
    }
    if (row.kind === 'unassigned') {
      onToggleUnassigned();
      return;
    }
    onTogglePerson(row.person);
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      disabled={disabled}
      options={rows}
      value={value}
      onChange={onChange}
      groupBy={(row) => (row.kind === 'unassigned' ? UNASSIGNED : row.role)}
      getOptionLabel={(row) => (row.kind === 'unassigned' ? 'Unassigned' : row.person.name)}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      data-testid="person-select"
      sx={{ minWidth: 210, maxWidth: 260 }}
      renderTags={() => (
        <Typography variant="body2" noWrap sx={{ pl: 0.75, maxWidth: 170 }} data-testid="person-select-summary">
          {summaryLabel(selected, unassigned)}
        </Typography>
      )}
      renderGroup={(params) =>
        // Unassigned is not a role: it sits above every heading, outside any group.
        params.group === UNASSIGNED ? (
          <React.Fragment key={params.key}>
            {params.children}
            <Divider component="li" />
          </React.Fragment>
        ) : (
          <li key={params.key} data-testid={`person-group-${params.group}`}>
            <Box
              sx={{
                position: 'sticky',
                top: -8,
                zIndex: 1,
                px: 1.5,
                py: 0.5,
                bgcolor: 'background.paper',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {roleMeta(params.group).heading}
            </Box>
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {params.children}
            </Box>
          </li>
        )
      }
      renderOption={(props, row) =>
        row.kind === 'unassigned' ? (
          <Box
            component="li"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            key={row.key}
            data-testid="person-option-unassigned"
            sx={{ gap: 0.75 }}
          >
            <Checkbox size="small" checked={unassigned} sx={{ p: 0.25 }} />
            <Typography variant="body2" noWrap>
              Unassigned
            </Typography>
          </Box>
        ) : (
          <Box
            component="li"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            key={row.key}
            data-testid={`person-option-${row.person.id}`}
            data-role={row.role}
            sx={{ gap: 0.75 }}
          >
            <Checkbox size="small" checked={selectedIds.has(row.person.id)} sx={{ p: 0.25 }} />
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {row.person.name}
            </Typography>
            {orderRoles(rolesOf(row.person)).map((r) => (
              <RoleChip key={r} role={r} />
            ))}
            {row.person.count !== undefined && (
              <Typography variant="caption" color="text.secondary">
                {row.person.count}
              </Typography>
            )}
          </Box>
        )
      }
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} placeholder={value.length ? '' : 'All people'} inputProps={{ ...params.inputProps, 'aria-label': 'People' }} />
      )}
    />
  );
}
